import { ID, IDPrefixes, RelationshipUpdate, UpdateGoal } from '@griply/types';
import {
  buildLinkUpdates,
  createPrefixId,
  LinkDocumentSide,
  sameSet,
} from '@griply/utils';

export type BuildGoalParentLinkWritesOptions = {
  goalId: ID;
  parentIds: ID[];
  currentParentIds: ID[];
  // False until this goal's parents have been written to its relationship document.
  isMigrated?: boolean;
};

export type GoalParentLinkWrites = {
  goalUpdate: UpdateGoal | null;
  relationshipUpdates: RelationshipUpdate[];
};

export const buildGoalParentLinkWrites = ({
  goalId,
  parentIds,
  currentParentIds,
  isMigrated = false,
}: BuildGoalParentLinkWritesOptions): GoalParentLinkWrites => {
  // An unmigrated goal's parents live in the frozen chain only — the relationship doc holds nothing to diff against.
  const relationshipUpdates = buildLinkUpdates({
    linkingId: createPrefixId(goalId, IDPrefixes.Goal),
    targetPrefix: IDPrefixes.Goal,
    desiredIds: parentIds,
    currentIds: isMigrated ? currentParentIds : [],
    side: LinkDocumentSide.Linking,
  });

  // Flipping the flag with no links written would drop the chain parents, so an unchanged set is a no-op.
  if (
    !relationshipUpdates.length &&
    (isMigrated || sameSet(parentIds, currentParentIds))
  ) {
    return { goalUpdate: null, relationshipUpdates: [] };
  }

  // The flag is the only thing written to the goal document — `ancestorIds` stays frozen so
  // older clients keep resolving the hierarchy they already know.
  return {
    goalUpdate: isMigrated ? null : { id: goalId, parentLinksMigrated: true },
    relationshipUpdates,
  };
};
